/**
 * @file /study/:familyId/sources/:sourceId — 本だなの1冊を読む。
 *
 * @what: どちらの読み方も画面いっぱいのリーダー (FullscreenViewer の immersive) で出す。
 *   **問題ごと** は取り込んだ1問 = 1画像をめくり、サイドペインの「この問」に読み取り内容と
 *   解いた結果と読み取りの確認を、「しぼりこみ」に学校・単元・確認状態・解いた結果の
 *   しぼりこみと読み方の切り替えを置く。**ページで読む** は紙1ページ = 1画像を目次で
 *   めくる (SourcePageReader)。どちらもメモと手書きのペインを持つ。
 *   問題ごとでは、まなぶ人を選んで解いた結果 (○ 解けた / △ あやしい / × 解けなかった) を
 *   残し、「まだ解いていない」「解けなかった」で絞れる (hooks/useItemResults、`?solved=`)。
 *   問の出典 (p.020) からそのページへ、ページからその面の問へ行き来でき、別冊 (解答例) の
 *   該当ページにも飛べる。出典の番号は紙に刷られた番号なので、ページ画像・メモ・AI の欄・
 *   復習カードを引くときは資料の reader_offset で連番へ直し、人へ見せる番号は刷られた番号に
 *   する (model/sourcePages.ts)。資料どうしの対応づけ (related) があれば、問から相手の資料の
 *   該当ページ (過去問 → テキスト、塾技 → 演習書) へ、ページからはその語を持つ相手の問へ
 *   行ける (model/sourceLinks.ts)。開いているページは `?page=`、問題ごとで読んでいることは
 *   `?view=items` に持つ。← → キーでもめくれる。
 *   ヘッダーの「記録する」から、開いていたページを初期値に学習の記録シート
 *   (StudyLogSheet) を開ける。さくいんと問題ごとの解説へは、ペインの下部から行く。
 * @why: 入試問題集は「どの学校がいつ何をどの水準で問うか」を確かめるための資料なので、
 *   ページ順に読むだけでなく学校と単元から引ける必要がある (代表の判断、2026-08-22)。
 *   巻頭の読み物や学校ごとの考査の本文は問題ごとの切り出しに入らないので、ページで読む。
 *   資料に pages があればページで読め、items があれば問題集の見せ方になり、両方あれば
 *   切り替える (epic_23 §8。形式の列挙型は持たず、何が入っているかで決める)。両方あるときは
 *   ページで読むから始め、問題ごとへは切り替えと絞り込みのリンクから入る (代表の判断、
 *   2026-09-12。読み方の決め方は model/readerMode.ts)。
 *   読む面の外側 (題と本だなへのリンク) を持たないのは、紙面をいちばん広く出すためである
 *   (代表の判断、2026-09-09。ページで読む側を先にリーダーへ寄せ、問題ごとも同じ土台に移した)。
 * @failure: 所属外の familyId は拒否する。取り込みが無い資料は案内だけを出す (本だなへ
 *   戻れる面にする)。問とページのどちらかが読めなければエラーにする (片方だけ出すと
 *   「無い」と見分けがつかない)。画像の取得失敗はその1枚だけエラーを出す。確認の保存に
 *   失敗したときは文言だけを出す (詳細は console)。
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  fetchScanItems,
  orderedIndexEntries,
  putScanReview,
  type StudyScanItem,
  type StudyScanReviewStatus,
} from "@/features/study/api/scans";
import { familyLabel, roleLabel } from "@/features/study/api/families";
import type { StudySource } from "@/features/study/api/sources";
import { aidsForSource } from "@/features/study/aids/registry";
import {
  FullscreenViewer,
  type ViewerControl,
  type ViewerPanel,
} from "@/features/study/components/FullscreenViewer";
import { ReaderPlainBar } from "@/features/study/components/ReaderPlainBar";
import { ClockIcon, SelectPageIcon, SparkIcon } from "@/features/study/components/readerIcons";
import { ReviewCardForm } from "@/features/study/components/ReviewCardForm";
import type { StudyReviewCardRef } from "@/features/study/api/reviewCards";
import { SourcePageReader } from "@/features/study/components/SourcePageReader";
import { panelIsOffPlain, wideScreen } from "@/features/study/lib/wideScreen";
import { AskBar } from "@/features/study/components/AskBar";
import {
  addTarget,
  pageTarget,
  removeTarget,
  type AskTarget,
} from "@/features/study/model/askSelection";
import { PageAskDialog } from "@/features/study/guides/PageAskDialog";
import { usePageAskCounts } from "@/features/study/guides/pageAskCounts";
import { problemIdsAtPage } from "@/features/study/guides/pageProblems";
import { useStudyWork } from "@/features/study/hooks/useStudyWork";
import { StudyLogSheet } from "@/features/study/components/StudyLogSheet";
import { StudyNotePane } from "@/features/study/components/StudyNotePane";
import { buildSchoolOptions, buildUnitOptions } from "@/features/study/model/readerFilters";
import {
  READER_VIEW_ITEMS,
  readerModeOf,
  readerPageParam,
  type ReaderMode,
} from "@/features/study/model/readerMode";
import {
  companionPageFor,
  companionsOf,
  pageFromSourceRef,
  printedPageOf,
  readerPageOf,
} from "@/features/study/model/sourcePages";
import {
  forwardLinks,
  referrersOf,
  reverseItems,
  type LinkTarget,
} from "@/features/study/model/sourceLinks";
import { useRelatedItems } from "@/features/study/hooks/useRelatedItems";
import { useItemResults } from "@/features/study/hooks/useItemResults";
import { useLearnerSelection } from "@/features/study/hooks/useLearnerSelection";
import { useResumePage } from "@/features/study/hooks/useReadingPositions";
import { useReadingSessionWriter } from "@/features/study/hooks/useReadingSessionWriter";
import { usePlannedBooks } from "@/features/study/hooks/usePlannedBooks";
import { fractionToPages } from "@/features/study/model/plannedRanges";
import { LearnerTabs } from "@/features/study/components/LearnerTabs";
import { memberLabel } from "@/features/study/api/members";
import { formatLogDate } from "@/features/study/api/dates";
import {
  ITEM_RESULT_FILTERS,
  ITEM_RESULT_LABELS,
  ITEM_RESULT_MARKS,
  countByResult,
  isItemResultFilter,
  matchesResultFilter,
  type ItemResultFilter,
} from "@/features/study/model/itemResults";
import { useSourceIndexes } from "@/features/study/hooks/useSourceIndexes";
import { useSourcePageLinks } from "@/features/study/hooks/useSourcePageLinks";
import { pageLinkReferrersOf, relatedPagesAt } from "@/features/study/model/pageLinks";
import { attainmentModel } from "@/features/study/model";
import { useMyFamilies } from "@/features/study/hooks/useMyFamilies";
import { useScanImage } from "@/features/study/hooks/useScanImage";
import { prefetchPageImage } from "@/features/study/lib/pageImageCache";
import { useSourcePages } from "@/features/study/hooks/useSourcePages";
import { useStudyBackground } from "@/features/study/hooks/useStudyBackground";
import { useStudyNotes } from "@/features/study/hooks/useStudyNotes";
import { useStudySources } from "@/features/study/hooks/useStudySources";
import { notesForLearner } from "@/features/study/model/studyNoteContext";
import { useHeaderFamily } from "@/shared/ui/headerContext";
import "@/features/study/components/study.css";

type ItemsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; items: StudyScanItem[] };

const REVIEW_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "すべて" },
  { value: "unchecked", label: "未確認" },
  { value: "wrong", label: "誤読あり" },
  { value: "ok", label: "合っている" },
];

/**
 * リーダーを出せないときの面である (読み込み中・取り込みなし・読み込み失敗)。
 *
 * @what: 本だなへ戻るリンクと資料の題を出し、渡された文言をその下に置く。
 * @why: リーダーは画面を覆うので、出せないときは本だなへ戻る道が必要である。
 * @failure: 描画のみ。
 */
function ReaderNotice({
  backHref,
  title,
  children,
}: {
  backHref: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="study-scope study-page">
      <div className="study-reader-header">
        <Link to={backHref}>← 本だなにもどる</Link>
        <h1 style={{ margin: 0 }}>{title}</h1>
      </div>
      {children}
    </section>
  );
}

function ReaderBody({
  familyId,
  source,
  sources,
  backHref,
  onOpenLog,
  panelFooter,
}: {
  familyId: string;
  source: StudySource;
  /** 本だなの全資料 (別冊と本体を引くため)。 */
  sources: readonly StudySource[];
  /** リーダーの「本だなにもどる」の行き先である。 */
  backHref: string;
  /** リーダーのヘッダーの「記録する」。 */
  onOpenLog: () => void;
  /** リーダーの目次パネルの下部に置くリンク (さくいん・問題ごとの解説など)。 */
  panelFooter: ReactNode;
}) {
  const sourceId = source.id;
  const [itemsState, setItemsState] = useState<ItemsState>({ status: "loading" });
  const pagesState = useSourcePages(familyId, sourceId);
  const [params, setParams] = useSearchParams();
  const [pos, setPos] = useState(0);
  const [noteDraft, setNoteDraft] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  // 問題ごとのサイドペインの切り替え (メモ / 手書き / この問 / しぼりこみ)。広い画面は問の内容から始める。
  const [activePanel, setActivePanel] = useState<string | null>(() => (wideScreen() ? "item" : null));
  // 「選んで聞く」で選んでいるもの。読み方を切り替えても持ち越す (同じ本の中の選択なので)。
  const [askTargets, setAskTargets] = useState<readonly AskTarget[]>([]);
  // 聞くことは、対象を選び直しても消さない (対象を入れ替えながら1つの問いを練るため)。
  const [askQuestion, setAskQuestion] = useState("");
  // 補足すること (本人が述べたこと)。AI の答えとは別に記録へ残る。
  const [askNote, setAskNote] = useState("");
  // 聞いた回数を持ち、増えるたびにページの AI 出力の件数を数え直す。
  const [askedCount, setAskedCount] = useState(0);
  // 選んだものは残したまま、質問の欄だけたたんでいるか (「読むだけにもどる」で立つ)。
  // ※なぜなら聞いたあとに欄が残ると紙面が縮み、読む画面に戻る道も分かりにくいためである。
  const [askCollapsed, setAskCollapsed] = useState(false);
  const addAskTarget = useCallback((t: AskTarget) => {
    // 新しく選んだら欄を戻す (選んだのに欄が出ないと、次に何をするか分からなくなる)。
    setAskCollapsed(false);
    setAskTargets((prev) => addTarget(prev, t));
  }, []);
  const removeAskTarget = useCallback((ref: string) => setAskTargets((prev) => removeTarget(prev, ref)), []);
  const clearAskTargets = useCallback(() => {
    setAskTargets([]);
    setAskQuestion("");
    setAskNote("");
    setAskCollapsed(false);
  }, []);
  /** 押されているものはもう一度押すと外れる (ヘッダーの「このページを選ぶ」)。 */
  const toggleAskTarget = useCallback((t: AskTarget) => {
    setAskCollapsed(false);
    setAskTargets((prev) =>
      prev.some((x) => x.ref === t.ref) ? removeTarget(prev, t.ref) : addTarget(prev, t),
    );
  }, []);
  const collapseAsk = useCallback(() => setAskCollapsed(true), []);
  const showAsk = useCallback(() => setAskCollapsed(false), []);
  // 復習カードを作る枠 (出典と問いの初期値)。
  const [cardDraft, setCardDraft] = useState<{
    ref: StudyReviewCardRef;
    label: string;
    prompt: string;
  } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchScanItems(familyId, sourceId, controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) setItemsState({ status: "ready", items });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load scan items", err);
        setItemsState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, sourceId]);

  const allItems = useMemo(
    () => (itemsState.status === "ready" ? itemsState.items : []),
    [itemsState],
  );
  const pages = useMemo(
    () => (pagesState.status === "ready" ? pagesState.pages : []),
    [pagesState],
  );
  const wantedPage = readerPageParam(params);
  // 両方あればページで読むから始め、問題ごとの印 (`view=items`・絞り込み) があるときだけ問題ごとに
  // する (model/readerMode.ts)。
  const mode: ReaderMode = readerModeOf(params, {
    pages: pages.length,
    items: allItems.length,
  });
  const companions = useMemo(() => companionsOf(sources, sourceId), [sources, sourceId]);

  // 資料どうしの対応づけ。問 → 相手のページ には相手の索引、ページ → 相手の問 には
  // 自分の索引と、自分を相手に指定している資料の問が要る。
  const relatedIds = useMemo(
    () => (source.related ?? []).map((r) => r.source_id).filter((id, i, a) => a.indexOf(id) === i),
    [source.related],
  );
  const referrers = useMemo(() => referrersOf(sources, sourceId), [sources, sourceId]);
  const referrerIds = useMemo(() => referrers.map((s) => s.id), [referrers]);
  const indexIds = useMemo(
    () => (referrerIds.length > 0 ? [sourceId, ...relatedIds] : relatedIds),
    [sourceId, relatedIds, referrerIds.length],
  );
  const indexes = useSourceIndexes(familyId, indexIds);
  const referrerItems = useRelatedItems(familyId, referrerIds);
  const linkTargets = useMemo(() => {
    const map = new Map<string, LinkTarget>();
    for (const id of relatedIds) {
      const target = sources.find((s) => s.id === id);
      if (target) map.set(id, { source: target, index: indexes.get(id) ?? [] });
    }
    return map;
  }, [relatedIds, sources, indexes]);
  const reverseFor = useCallback(
    (page: number) =>
      reverseItems(
        source,
        indexes.get(sourceId) ?? [],
        page,
        referrers.map((s) => ({ source: s, items: referrerItems.get(s.id) ?? [] })),
      ),
    [source, indexes, sourceId, referrers, referrerItems],
  );

  // ページの対応は、自分が持つものと自分を相手に持つ資料のものを、資料を開いたときだけ取る。
  const pageLinkReferrers = useMemo(() => pageLinkReferrersOf(sources, sourceId), [sources, sourceId]);
  const pageLinkIds = useMemo(
    () => [
      ...((source.page_link_targets ?? []).length > 0 ? [sourceId] : []),
      ...pageLinkReferrers.map((s) => s.id),
    ],
    [source.page_link_targets, sourceId, pageLinkReferrers],
  );
  const pageLinks = useSourcePageLinks(familyId, pageLinkIds);
  const relatedPagesFor = useCallback(
    (page: number) =>
      relatedPagesAt(
        source,
        pageLinks.get(sourceId) ?? [],
        pageLinkReferrers.map((s) => ({ source: s, links: pageLinks.get(s.id) ?? [] })),
        sources,
        page,
      ),
    [source, pageLinks, sourceId, pageLinkReferrers, sources],
  );

  const { options: unitOptions, match: unitMatch } = useMemo(
    () => buildUnitOptions(attainmentModel, allItems),
    [allItems],
  );
  const schoolOptions = useMemo(() => buildSchoolOptions(allItems), [allItems]);

  const schoolFilter = params.get("school") ?? "";
  const reviewFilter = params.get("review") ?? "";
  const solvedParam = params.get("solved");
  const solvedFilter: ItemResultFilter = isItemResultFilter(solvedParam) ? solvedParam : "";

  // 解いた結果と学びメモは学習者ごと。まなぶ人は端末が覚えている人を既定にし、ヘッダーにも出す。
  const { learners, learnerId, selectLearner, ready: learnerReady } = useLearnerSelection(familyId);
  // ページの指定が無くページで読むときは、前回開いていたページから始める (閲覧位置は端末をまたいで
  // 共有する)。読み終えるまで紙面を出さない。※なぜなら先に先頭のページを出すと、それを閲覧位置として
  // 送り、前回のページを上書きするためである (代表の判断、2026-09-14)。
  const needsResume = wantedPage === null && mode === "pages" && pages.length > 0;
  const resume = useResumePage(
    familyId,
    sourceId,
    { ready: learnerReady, personId: learnerId },
    source.page_count,
    needsResume,
  );
  const resumePage = resume.status === "ready" ? resume.page : null;
  useEffect(() => {
    if (!needsResume || resumePage === null) return;
    const next = new URLSearchParams(params);
    next.set("page", String(resumePage));
    setParams(next, { replace: true });
  }, [needsResume, resumePage, params, setParams]);
  const resuming = needsResume && (resume.status !== "ready" || resumePage !== null);
  const results = useItemResults(familyId, learnerId, sourceId);
  // 対応する作品があれば、いまのページの問題にふりかえりを結ぶ (問題ごとの解説の設計 §6.1)。
  // 作品が無い資料でも、ページに紐づけたふりかえりは読めるようにする。
  // ※なぜなら作品 (問題ごとの解説) を持つ資料は一部で、それ以外の本のふりかえりが
  //   CLI からしか読めない状態になるためである。
  const work = useStudyWork(source.work_id ?? "");
  const currentReaderPage = wantedPage ?? pages[0]?.page;
  // 共通データ (問題一覧) だけが刷られた番号を持つので、そこへ問い合わせるときだけ換算する。
  const printedPage =
    currentReaderPage !== undefined ? printedPageOf(source, currentReaderPage) : undefined;
  // このページに紐づくまなぶ人のメモ。メモを対象にした AI 出力も、そのページの欄に出すために渡す。
  const allNotes = useStudyNotes(familyId);
  const noteIdsAt = useCallback(
    (readerPage: number) =>
      notesForLearner(allNotes, learnerId)
        .filter((n) =>
          (n.refs ?? []).some(
            (r) => r.kind === "source" && r.source_id === sourceId && r.page === readerPage,
          ),
        )
        .map((n) => n.id),
    [allNotes, learnerId, sourceId],
  );
  const resultsState = results.state;
  const resultItems = useMemo(
    () => (resultsState.status === "ready" ? resultsState.items : {}),
    [resultsState],
  );
  const unitFilter = params.get("unit")
    ? `u:${params.get("unit")}`
    : params.get("tax")
      ? `t:${params.get("tax")}`
      : "";
  const wantedItemId = params.get("item");

  const items = useMemo(() => {
    const unitIds = unitFilter ? unitMatch.get(unitFilter) : undefined;
    return allItems.filter((i) => {
      if (schoolFilter && i.index["出典校"]?.trim() !== schoolFilter) return false;
      if (reviewFilter && i.review_status !== reviewFilter) return false;
      if (unitFilter && !unitIds?.has(i.id)) return false;
      if (solvedFilter && !matchesResultFilter(resultItems, i.id, solvedFilter)) return false;
      return true;
    });
  }, [allItems, schoolFilter, reviewFilter, unitFilter, unitMatch, solvedFilter, resultItems]);

  const resultCounts = useMemo(
    () =>
      countByResult(
        resultItems,
        allItems.map((i) => i.id),
      ),
    [resultItems, allItems],
  );

  const filtering =
    schoolFilter !== "" || reviewFilter !== "" || unitFilter !== "" || solvedFilter !== "";

  /** しぼりこみを 1 つ変える (他の条件と ?item= は残さない)。 */
  const setFilter = (key: "school" | "review" | "unit" | "solved", value: string) => {
    const next = new URLSearchParams(params);
    next.delete("item");
    if (key === "unit") {
      next.delete("unit");
      next.delete("tax");
      if (value.startsWith("u:")) next.set("unit", value.slice(2));
      else if (value.startsWith("t:")) next.set("tax", value.slice(2));
    } else if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setParams(next, { replace: true });
    setPos(0);
  };

  const clearFilters = () => {
    setParams(new URLSearchParams(), { replace: true });
    setPos(0);
  };

  // ?item= が指す問に合わせる。しぼりこみを変えたら先頭に戻る。
  useEffect(() => {
    if (!wantedItemId) return;
    const at = items.findIndex((i) => i.id === wantedItemId);
    if (at >= 0) setPos(at);
  }, [wantedItemId, items]);

  // しぼりこみの結果より後ろを指していたら詰める。
  useEffect(() => {
    setPos((p) => (items.length === 0 ? 0 : Math.min(p, items.length - 1)));
  }, [items.length]);

  const current = items[pos] ?? null;

  useEffect(() => {
    setNoteDraft(current?.review_note ?? "");
    setSaveError(null);
  }, [current?.id, current?.review_note]);

  const image = useScanImage(current?.image_path ?? null, current?.content_type ?? "image/png");

  // 隣の問も先読みして、← → の行き来を取得待ちなしにする (進む方向を優先)
  useEffect(() => {
    for (const n of [pos + 1, pos + 2, pos - 1]) {
      const it = items[n];
      if (it?.image_path) prefetchPageImage(it.image_path, it.content_type ?? "image/png");
    }
  }, [pos, items]);

  const counts = useMemo(() => {
    const c = { ok: 0, wrong: 0, unchecked: 0 };
    for (const i of allItems) c[i.review_status]++;
    return c;
  }, [allItems]);

  const goto = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      setPos(Math.max(0, Math.min(items.length - 1, next)));
    },
    [items.length],
  );

  useEffect(() => {
    if (mode !== "items") return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT")
      )
        return;
      if (e.key === "ArrowLeft") goto(pos - 1);
      if (e.key === "ArrowRight") goto(pos + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goto, pos, mode]);

  /** ページで読むへ切り替える (いまの問のページがあればそこを開く)。他の条件は残す。 */
  const openPage = (page: number | null) => {
    const next = new URLSearchParams(params);
    next.set("page", String(page ?? pages[0]?.page ?? 1));
    // 問題ごとの印は外す (ページを指したほうを先に見るので、残しても読み方は変わらないが、
    // 共有された URL から印だけが残るのを防ぐ)。
    next.delete("view");
    setParams(next, { replace: true });
  };

  /** 問題ごとへ戻す。itemId を渡せばその問を開く。 */
  const openItems = (itemId?: string) => {
    const next = new URLSearchParams(params);
    next.delete("page");
    // ページで読むが既定なので、問題ごとは印を残す。消すと同じ面に戻るだけになる。
    next.set("view", READER_VIEW_ITEMS);
    if (itemId) {
      next.delete("school");
      next.delete("unit");
      next.delete("tax");
      next.delete("review");
      next.set("item", itemId);
    }
    setParams(next, { replace: true });
  };

  // 問の出典 (「… p.020」) は紙に刷られた番号である。ページ画像・メモ・AI の欄・復習カードは
  // どれもリーダーの連番で引くので、ここで一度だけ連番へ直し、人へ見せる番号は刷られた番号を使う。
  // ※なぜなら片方だけを換算すると、同じ問を見ていてもメモと AI の欄が別のページを指すためである。
  const itemPrintedPage = current ? pageFromSourceRef(current.index) : null;
  const itemReaderPage =
    itemPrintedPage === null ? null : readerPageOf(source, itemPrintedPage);
  const currentPageExists =
    itemReaderPage !== null && pages.some((p) => p.page === itemReaderPage);
  // どちらの読み方でも、いま出している面のページで AI 出力を数える。
  const askPage = resuming ? null : mode === "pages" ? (currentReaderPage ?? null) : itemReaderPage;
  // 読んだ時間を測る。どちらの読み方でも、いま出している面のページに滞在を付ける。付属音声が鳴っている間は
  // 操作が無くても数える (ページで読む面が知らせる)。
  const [audible, setAudible] = useState(false);
  useReadingSessionWriter(familyId, learnerId, sourceId, askPage, audible);
  // 計画に入っている本なら、今週の範囲を目次のパネルに出す。ISBN の無い資料は計画と結べないので読まない
  const planned = usePlannedBooks(familyId, learnerId, source.isbn !== undefined);
  const plannedRange =
    planned.status === "ready" && source.isbn !== undefined
      ? planned.ranges?.get(source.isbn)
      : undefined;
  const weekRange =
    plannedRange?.fraction && (source.page_count ?? pages.length) > 0
      ? fractionToPages(plannedRange.fraction, source.page_count ?? pages.length)
      : undefined;
  const askPrintedPage = mode === "pages" ? (printedPage ?? null) : itemPrintedPage;
  const askNoteIds = useMemo(
    () => (askPage === null ? [] : noteIdsAt(askPage)),
    [noteIdsAt, askPage],
  );
  const askProblemIds = useMemo(
    () =>
      askPrintedPage === null || work.status !== "ready"
        ? []
        : problemIdsAtPage(work.index, askPrintedPage),
    [work, askPrintedPage],
  );
  // reloadKey は聞くたびに増える数で、これを渡すと件数を数え直す。渡さないと、聞いた直後は
  // 0件のままになり、読み込み直すまで出てこない (PR #757)。
  const askCounts = usePageAskCounts({
    familyId,
    personId: learnerId ?? "",
    sourceId,
    page: askPage ?? 0,
    noteIds: askNoteIds,
    problemIds: askProblemIds,
    reloadKey: askedCount,
  });
  // 開いている AI 出力のモーダル。ページを送ったら閉じる (別のページの記録を同じ題で出さない)。
  const [openAskPreset, setOpenAskPreset] = useState<string | null>(null);
  useEffect(() => {
    setOpenAskPreset(null);
  }, [askPage]);
  const askControls: ViewerControl[] =
    askPage === null || learnerId === null
      ? []
      : askCounts.map((c) => ({
          id: `ask:${c.preset}`,
          label: c.label,
          count: c.count,
          icon: <SparkIcon />,
          tone: "ai" as const,
          group: "AI が答えたこと",
          onSelect: () => setOpenAskPreset(c.preset),
        }));
  const openAsk = askCounts.find((c) => c.preset === openAskPreset) ?? null;
  const askDialog =
    openAsk !== null && askPage !== null && askPrintedPage !== null && learnerId !== null ? (
      <PageAskDialog
        familyId={familyId}
        personId={learnerId}
        sourceId={sourceId}
        page={askPage}
        printedPage={askPrintedPage}
        noteIds={askNoteIds}
        problemIds={askProblemIds}
        presetId={openAsk.preset}
        label={openAsk.label}
        onClose={() => setOpenAskPreset(null)}
      />
    ) : null;
  const currentSchool = current?.index["出典校"]?.trim() ?? "";
  const companionLinks = currentSchool
    ? companions
        .map((c) => ({ companion: c, page: companionPageFor(c, currentSchool) }))
        .filter((x): x is { companion: StudySource; page: number } => x.page !== null)
    : [];
  const relatedLinks = current ? forwardLinks(current, source, linkTargets) : [];
  const currentResult = current ? resultItems[current.id] : undefined;

  /** 解いた結果を残し、次の問へ進む (絞り込みの結果は再計算されるので、いまの位置のまま次を見る)。 */
  const solve = async (result: "ok" | "partial" | "ng") => {
    if (!current) return;
    const ok = await results.record(current.id, result);
    if (ok && pos < items.length - 1) setPos(pos + 1);
  };

  const review = async (status: StudyScanReviewStatus) => {
    if (!current || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body: { status: StudyScanReviewStatus; note?: string } = { status };
      if (noteDraft.trim()) body.note = noteDraft.trim();
      const updated = await putScanReview(familyId, sourceId, current.id, body);
      setItemsState((prev) =>
        prev.status === "ready"
          ? { ...prev, items: prev.items.map((i) => (i.id === updated.id ? updated : i)) }
          : prev,
      );
      const nextUnchecked = items.findIndex(
        (i, idx) => idx > pos && i.review_status === "unchecked",
      );
      if (nextUnchecked >= 0) setPos(nextUnchecked);
    } catch (err: unknown) {
      console.error("[study] failed to save scan review", err);
      setSaveError("保存できませんでした。時間を置いて再度お試しください。");
    } finally {
      setSaving(false);
    }
  };

  if (itemsState.status === "loading" || pagesState.status === "loading") {
    return (
      <ReaderNotice backHref={backHref} title={source.title}>
        <p role="status" className="study-muted">
          取り込んだ内容を読み込んでいます...
        </p>
      </ReaderNotice>
    );
  }
  if (itemsState.status === "error" || pagesState.status === "error") {
    return (
      <ReaderNotice backHref={backHref} title={source.title}>
        <p role="alert" className="study-error">
          取り込んだ内容を読み込めませんでした。時間を置いて再読み込みしてください。
        </p>
      </ReaderNotice>
    );
  }
  if (allItems.length === 0 && pages.length === 0) {
    return (
      <ReaderNotice backHref={backHref} title={source.title}>
        <p className="study-muted">
          この資料にはまだ取り込みがありません。スキャンを取り込むとここで読めるようになります。
        </p>
        {/* 取り込みが無い本はリーダーが出ないので、記録と読みものへの入口をここに置く */}
        <p className="study-reader-aids">
          <button
            type="button"
            className="study-ghost-btn"
            aria-label={`${source.title} の学習を記録する`}
            onClick={onOpenLog}
          >
            記録する
          </button>
        </p>
        {panelFooter}
      </ReaderNotice>
    );
  }

  if (resuming) {
    return (
      <ReaderNotice backHref={backHref} title={source.title}>
        <p role="status" className="study-muted">
          前回開いていたページを読み込んでいます...
        </p>
      </ReaderNotice>
    );
  }

  const modeSwitch =
    allItems.length > 0 && pages.length > 0 ? (
      <div className="study-reader-modes" role="group" aria-label="読み方">
        <button
          type="button"
          className="study-reader-mode-btn"
          aria-pressed={mode === "items"}
          onClick={() => openItems()}
        >
          問題ごと（{allItems.length}）
        </button>
        <button
          type="button"
          className="study-reader-mode-btn"
          aria-pressed={mode === "pages"}
          onClick={() => openPage(currentPageExists ? itemReaderPage : null)}
        >
          ページで読む（{pages.length}）
        </button>
      </div>
    ) : null;

  const cardForm = cardDraft ? (
    <ReviewCardForm
      familyId={familyId}
      cardRef={cardDraft.ref}
      refLabel={cardDraft.label}
      defaultPrompt={cardDraft.prompt}
      onClose={() => setCardDraft(null)}
    />
  ) : null;

  if (mode === "pages") {
    return (
      <>
        <SourcePageReader
          familyId={familyId}
          source={source}
          sources={sources}
          pages={pages}
          items={allItems}
          wantedPage={wantedPage}
          onGoto={(page) => openPage(page)}
          onOpenItem={(itemId) => openItems(itemId)}
          relatedItemsAt={referrers.length > 0 ? reverseFor : undefined}
          relatedPagesAt={pageLinkIds.length > 0 ? relatedPagesFor : undefined}
          learnerPersonId={learnerId}
          learners={learners}
          weekRange={weekRange}
          onAudibleChange={setAudible}
          onMakeCard={(page) =>
            setCardDraft({
              // 紐づけは連番、ラベルは人が突き合わせる刷られた番号にする。
              ref: { source_id: sourceId, page },
              label: `${source.title} p.${printedPageOf(source, page)}`,
              prompt: "",
            })
          }
          askControls={askControls}
          askTargets={askTargets}
          askQuestion={askQuestion}
          onAskQuestionChange={setAskQuestion}
          askNote={askNote}
          onAskNoteChange={setAskNote}
          onAsked={() => setAskedCount((n) => n + 1)}
          onAddTarget={addAskTarget}
          onToggleTarget={toggleAskTarget}
          onRemoveTarget={removeAskTarget}
          onClearTargets={clearAskTargets}
          askCollapsed={askCollapsed}
          onCollapseAsk={collapseAsk}
          onShowAsk={showAsk}
          backHref={backHref}
          onOpenLog={onOpenLog}
          panelFooter={
            <>
              {modeSwitch}
              {panelFooter}
            </>
          }
        />
        {askDialog}
        {cardForm}
      </>
    );
  }

  const itemPane = current ? (
    <div className="study-viewer-panel">
      <div className="study-reader-item-head">
        <span className="study-reader-item-id">{current.id}</span>
        {current.review_status === "ok" ? (
          <span className="study-review-badge study-review-badge--ok">合っている</span>
        ) : current.review_status === "wrong" ? (
          <span className="study-review-badge study-review-badge--wrong">誤読あり</span>
        ) : (
          <span className="study-review-badge">未確認</span>
        )}
      </div>
      <dl className="study-reader-index">
        {orderedIndexEntries(current.index).map(([k, v]) => (
          <div key={k} className="study-reader-index-row">
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <div className="study-reader-solve">
        <div className="study-reader-review-title">解いた結果</div>
        {learners.length > 1 ? (
          <LearnerTabs learners={learners} selectedId={learnerId} onSelect={selectLearner} />
        ) : null}
        {results.state.status === "error" ? (
          <p role="alert" className="study-error">
            解いた結果を読み込めませんでした。時間を置いて再読み込みしてください。
          </p>
        ) : null}
        {currentResult ? (
          <p className="study-reader-solve-last">
            {formatLogDate(currentResult.last.date, false)} に{" "}
            {ITEM_RESULT_MARKS[currentResult.last.result]}{" "}
            {ITEM_RESULT_LABELS[currentResult.last.result]}
            {currentResult.count > 1 ? `（${currentResult.count} 回目）` : ""}
          </p>
        ) : learnerId ? (
          <p className="study-muted">まだ解いていません。</p>
        ) : null}
        <div className="study-reader-solve-actions">
          {(["ok", "partial", "ng"] as const).map((r) => (
            <button
              key={r}
              type="button"
              className={`study-review-btn study-review-btn--${r === "ok" ? "ok" : r === "ng" ? "wrong" : "hard"}`}
              disabled={results.busy || !learnerId}
              onClick={() => void solve(r)}
              aria-label={`${learners.find((l) => l.person_id === learnerId) ? memberLabel(learners.find((l) => l.person_id === learnerId)!) + " が" : ""}${ITEM_RESULT_LABELS[r]}`}
            >
              <span aria-hidden="true">{ITEM_RESULT_MARKS[r]}</span> {ITEM_RESULT_LABELS[r]}
            </button>
          ))}
          {results.actionError ? (
            <span role="alert" className="study-save-error">
              {results.actionError}
            </span>
          ) : null}
        </div>
      </div>
      <div className="study-reader-links">
        <button
          type="button"
          className="study-ghost-btn"
          onClick={() =>
            setCardDraft({
              ref: {
                source_id: sourceId,
                ...(itemReaderPage !== null ? { page: itemReaderPage } : {}),
                item_id: current.id,
              },
              label: `${source.title}${itemPrintedPage !== null ? ` p.${itemPrintedPage}` : ""}（${current.id}）`,
              prompt: current.index["設問"]?.trim() || current.index["指示"]?.trim() || "",
            })
          }
        >
          復習カードにする
        </button>
      </div>
      {relatedLinks.length > 0 ? (
        <div className="study-reader-links">
          <div className="study-reader-review-title">関連する資料</div>
          <ul className="study-link-list">
            {relatedLinks.map((l) => (
              <li key={`${l.source.id}-${l.page}`}>
                <Link
                  to={`/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(
                    l.source.id,
                  )}?page=${l.page}`}
                >
                  {l.source.title}（p.{l.page} {l.label}）
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {currentPageExists || companionLinks.length > 0 ? (
        <ul className="study-link-list study-reader-links">
          {currentPageExists ? (
            <li>
              <button
                type="button"
                className="study-link-btn"
                onClick={() => openPage(itemReaderPage)}
              >
                このページを開く（p.{itemPrintedPage}）
              </button>
            </li>
          ) : null}
          {companionLinks.map(({ companion, page }) => (
            <li key={companion.id}>
              <Link
                to={`/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(
                  companion.id,
                )}?page=${page}`}
              >
                別冊「{companion.title}」（p.{printedPageOf(companion, page)}）
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="study-reader-review">
        <div className="study-reader-review-title">読み取りの確認</div>
        <input
          type="text"
          placeholder="誤読の指摘・修正メモ（任意）"
          aria-label="読み取りの修正メモ"
          maxLength={500}
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
        />
        <div className="study-reader-review-actions">
          <button
            type="button"
            className="study-review-btn study-review-btn--ok"
            disabled={saving}
            onClick={() => void review("ok")}
          >
            <span aria-hidden="true">✓</span> 合っている
          </button>
          <button
            type="button"
            className="study-review-btn study-review-btn--wrong"
            disabled={saving}
            onClick={() => void review("wrong")}
          >
            <span aria-hidden="true">✕</span> 誤読あり
          </button>
          {saveError ? (
            <span role="alert" className="study-save-error">
              {saveError}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  ) : null;

  const filterPane = (
    <div className="study-viewer-panel">
      <div className="study-reader-filters">
        {schoolOptions.length > 0 ? (
          <label className="study-reader-filter">
            <span>学校</span>
            <select value={schoolFilter} onChange={(e) => setFilter("school", e.target.value)}>
              <option value="">すべて（{schoolOptions.length} 校）</option>
              {schoolOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}（{o.count}）
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {unitOptions.length > 0 ? (
          <label className="study-reader-filter">
            <span>単元</span>
            <select value={unitFilter} onChange={(e) => setFilter("unit", e.target.value)}>
              <option value="">すべて（{unitOptions.length} 種類）</option>
              {unitOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}（{o.count}）
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="study-reader-filter">
          <span>確認</span>
          <select value={reviewFilter} onChange={(e) => setFilter("review", e.target.value)}>
            {REVIEW_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {learnerId ? (
          <label className="study-reader-filter">
            <span>解いた結果</span>
            <select value={solvedFilter} onChange={(e) => setFilter("solved", e.target.value)}>
              {ITEM_RESULT_FILTERS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}（{resultCounts[o.value]}）
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {filtering ? (
          <button type="button" className="study-ghost-btn" onClick={clearFilters}>
            しぼりこみを外す
          </button>
        ) : null}
      </div>
      <p className="study-reader-counts">
        確認済み {counts.ok + counts.wrong} / {allItems.length}
        {counts.wrong > 0 ? `（誤読 ${counts.wrong}）` : ""}
      </p>
      {modeSwitch}
      {panelFooter ? <div className="study-viewer-panel-footer">{panelFooter}</div> : null}
    </div>
  );

  // 問が0件のときは「この問」が無いので、選ばれていたら「しぼりこみ」へ倒す
  // (しぼりこみを外す道が閉じないようにする)。
  const shownPanel = itemPane === null && activePanel === "item" ? "filter" : activePanel;
  // 選ぶ対象のページは連番で持つ (itemReaderPage が出典の刷られた番号を直したもの)。
  const itemPageTarget = itemReaderPage === null ? null : pageTarget(source, itemReaderPage);
  const itemPanels: ViewerPanel[] = [
    { id: "note", label: "メモ", primary: true },
    { id: "ink", label: "手書き" },
    ...(itemPane ? [{ id: "item", label: "この問", content: itemPane }] : []),
    { id: "filter", label: "しぼりこみ", content: filterPane },
  ];

  return (
    <>
      <FullscreenViewer
        immersive
        image={image}
        alt={current ? `スキャン ${current.id}` : ""}
        label={source.title}
        {...(items.length === 0
          ? {
              stageMessage: (
                <p className="study-viewer-msg">
                  この条件にあう問はありません。しぼりこみを外すと全部の問に戻ります。
                </p>
              ),
            }
          : {})}
        headerExtra={
          <span className="study-viewer-pos">
            {items.length === 0 ? "0 / 0" : `${pos + 1} / ${items.length}`}
          </span>
        }
        plainAction={
          <ReaderPlainBar
            offPlain={panelIsOffPlain(shownPanel) || (askTargets.length > 0 && !askCollapsed)}
            onPlain={() => {
              setActivePanel(null);
              collapseAsk();
            }}
            askCount={askCollapsed ? askTargets.length : 0}
            onShowAsk={showAsk}
            onClearAsk={clearAskTargets}
          />
        }
        controls={[
          ...(itemPageTarget !== null
            ? [
                {
                  id: "pick-page",
                  label: "このページを選ぶ",
                  icon: <SelectPageIcon />,
                  group: "教材から選ぶ",
                  tone: "pick" as const,
                  primary: true,
                  pressed: askTargets.some((t) => t.ref === itemPageTarget.ref),
                  onSelect: () => toggleAskTarget(itemPageTarget),
                },
              ]
            : []),
          ...askControls,
          {
            id: "log",
            label: "記録する",
            ariaLabel: `${source.title} の学習を記録する`,
            icon: <ClockIcon />,
            group: "この本の操作",
            tone: "quiet" as const,
            onSelect: onOpenLog,
          },
        ]}
        closeLabel="本だなにもどる"
        shortCloseLabel="本だな"
        onClose={() => navigate(backHref)}
        onPrev={() => goto(pos - 1)}
        onNext={() => goto(pos + 1)}
        hasPrev={pos > 0}
        hasNext={items.length > 0 && pos < items.length - 1}
        bottomBar={
          askTargets.length > 0 && !askCollapsed ? (
            <AskBar
              familyId={familyId}
              learnerPersonId={learnerId}
              targets={askTargets}
              question={askQuestion}
              onQuestionChange={setAskQuestion}
              note={askNote}
              onNoteChange={setAskNote}
              onRemove={removeAskTarget}
              onClear={clearAskTargets}
              onAsked={() => setAskedCount((n) => n + 1)}
            />
          ) : null
        }
        panels={itemPanels}
        activePanelId={shownPanel}
        onPanelChange={setActivePanel}
        persistentPane={{
          visibleFor: ["note", "ink"],
          content: (
            <StudyNotePane
              familyId={familyId}
              source={source}
              sources={sources}
              {...(itemReaderPage !== null ? { page: itemReaderPage } : {})}
              learnerPersonId={learnerId}
              learners={learners}
              view={shownPanel === "ink" ? "ink" : "text"}
              onSelectTarget={addAskTarget}
            />
          ),
        }}
      />
      {askDialog}
      {cardForm}
    </>
  );
}
export function StudySourceReaderRoute() {
  const { familyId, sourceId } = useParams<{ familyId: string; sourceId: string }>();
  const state = useMyFamilies();
  useStudyBackground();
  const headerFamily =
    state.status === "ready" ? state.families.find((f) => f.family_id === familyId) : undefined;
  useHeaderFamily(
    headerFamily
      ? {
          label: familyLabel(headerFamily),
          role: roleLabel(headerFamily.role),
          switchable: state.status === "ready" && state.families.length > 1,
        }
      : null,
  );

  if (state.status === "loading") {
    return (
      <div
        className="study-scope"
        role="status"
        aria-live="polite"
        style={{ padding: "var(--space-5)" }}
      >
        読み込み中...
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <section className="study-scope study-page">
        <h1>いちごスタディ</h1>
        <p role="alert" className="study-error">
          家族の情報を読み込めませんでした。時間を置いて再読み込みしてください。
        </p>
      </section>
    );
  }
  const family = state.families.find((f) => f.family_id === familyId);
  if (!family || !familyId || !sourceId) {
    return (
      <section className="study-scope study-page">
        <h1>いちごスタディ</h1>
        <p role="alert" className="study-muted">
          この家族には所属していないか、見つかりません。
        </p>
        <Link to="/study">家族を選び直す</Link>
      </section>
    );
  }

  return <ReaderPage familyId={familyId} sourceId={sourceId} />;
}

/** 資料の情報を本だなから引いてから中身を描く (見出しに題と役割を出すため)。 */
function ReaderPage({ familyId, sourceId }: { familyId: string; sourceId: string }) {
  const sourcesState = useStudySources(familyId);
  const [params] = useSearchParams();
  const [logOpen, setLogOpen] = useState(false);
  const backHref = `/study/${encodeURIComponent(familyId)}?view=shelf`;
  const source =
    sourcesState.status === "ready"
      ? (sourcesState.sources.find((s) => s.id === sourceId) ?? null)
      : null;
  // ページで読んでいたら、そのページを記録の「終わり」の初期値にする。
  const pageParam = params.get("page");
  const readingPage = pageParam !== null && /^\d+$/.test(pageParam) ? Number(pageParam) : undefined;

  if (sourcesState.status === "ready" && source === null) {
    return (
      <ReaderNotice backHref={backHref} title="本だな">
        <p role="alert" className="study-muted">
          この資料は見つかりません。本だなから選び直してください。
        </p>
      </ReaderNotice>
    );
  }

  const aids = aidsForSource(sourceId);
  const readerHref = `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(sourceId)}`;
  // さくいんと、この本のために作った読みもの (タイムライン等) へは、リーダーのサイドペインの
  // 下部から行く (本だなのカードのメニューにも同じ入口がある)。
  const aidLinks =
    aids.length > 0 || source?.index_count || source?.work_id ? (
      <p className="study-reader-aids">
        {source?.index_count ? <Link to={`${readerHref}/index`}>さくいん</Link> : null}
        {aids.map((a) => (
          <Link key={a.id} to={`${readerHref}/aids/${a.id}`}>
            {a.title}
          </Link>
        ))}
        {source?.work_id ? <Link to={`${readerHref}/guides`}>問題ごとの解説</Link> : null}
      </p>
    ) : null;

  if (sourcesState.status === "error") {
    return (
      <ReaderNotice backHref={backHref} title="取り込んだ資料">
        <p role="alert" className="study-error">
          資料の情報を読み込めませんでした。時間を置いて再読み込みしてください。
        </p>
      </ReaderNotice>
    );
  }
  if (source === null || sourcesState.status !== "ready") {
    return (
      <ReaderNotice backHref={backHref} title="取り込んだ資料">
        <p role="status" className="study-muted">
          読み込み中...
        </p>
      </ReaderNotice>
    );
  }

  return (
    <div className="study-scope">
      <ReaderBody
        familyId={familyId}
        source={source}
        sources={sourcesState.sources}
        backHref={backHref}
        onOpenLog={() => setLogOpen(true)}
        panelFooter={aidLinks}
      />
      {logOpen ? (
        <StudyLogSheet
          familyId={familyId}
          source={{ id: source.id, title: source.title }}
          {...(readingPage !== undefined ? { defaultToPage: readingPage } : {})}
          onClose={() => setLogOpen(false)}
        />
      ) : null}
    </div>
  );
}
